import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@kalos/ui';
import { type FC, useState } from 'react';

import { ActivityCard } from '../../../components/ActivityCard';
import { useMembershipById } from '../../Memberships/hooks/useMembershipById';

type LogSource = 'job' | 'membership';

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: number;
  /** The job's membership; when set, a second tab shows that membership's activity. */
  membershipId?: number;
}

/**
 * Job Activity for the legacy job screen: the shared activity table scoped to the job, and to the
 * job's membership contract when it has one.
 */
export const ServiceCallLogs: FC<Props> = ({ open, onClose, eventId, membershipId = 0 }) => {
  const hasMembership = membershipId > 0;
  const [source, setSource] = useState<LogSource>('job');
  const membershipQuery = useMembershipById(membershipId);
  const contractId = membershipQuery.data?.contractId ?? 0;

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-2xl"
      >
        <SheetHeader>
          <SheetTitle>Job Activity</SheetTitle>
          <SheetDescription>Everything logged on job #{eventId}.</SheetDescription>
        </SheetHeader>
        {hasMembership && (
          <Tabs value={source} onValueChange={(v) => setSource(v as LogSource)}>
            <TabsList>
              <TabsTrigger value="job">Job</TabsTrigger>
              <TabsTrigger value="membership">Membership</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        {/* Keyed per source: the card owns its page, search and person filter, and the two
            tabs render it at the same position, so without a key the state would carry over. */}
        {hasMembership && source === 'membership' ? (
          contractId > 0 ? (
            <ActivityCard key="membership" contractId={contractId} />
          ) : (
            <p className="text-muted-foreground text-sm">
              {membershipQuery.isPending
                ? 'Loading membership…'
                : 'This membership has no contract to read activity from.'}
            </p>
          )
        ) : (
          <ActivityCard key="job" eventId={eventId} />
        )}
      </SheetContent>
    </Sheet>
  );
};
